<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import { supabase, useSupabase } from "../db/supabaseClient.js";
import ChatAssistant from "../components/ChatAssistant.vue";

const loading = ref(false);
const errorMsg = ref("");
const raw = ref([]); // [{ id, name, price, category, image }]
const activeCategory = ref("todos");

function calculateFinalPrice(unit, discount, taxRate) {
  const price = Number(unit || 0);
  const discCoeff = 1 - Math.min(Math.max(Number(discount || 0), 0), 100) / 100;
  const taxCoeff = taxRate != null ? 1 + Number(taxRate) / 100 : 1;
  return price * discCoeff * taxCoeff;
}

async function fetchMenu() {
  try {
    loading.value = true;
    errorMsg.value = "";
    if (!useSupabase()) {
      errorMsg.value = "Error de configuración: Supabase no disponible.";
      loading.value = false;
      return;
    }
    const { data: prods, error } = await supabase
      .from("products")
      .select("id:id_products,name,unit_price,discount,tax_id,category_id,active,description")
      .eq("active", true)
      .order("id_products", { ascending: true });

    if (error) throw error;

    const ids = (prods || []).map((p) => p.id);

    // Fetch Categories
    const { data: cats } = await supabase
      .from("categories")
      .select("id:id_categories,name");
    const catMap = new Map((cats || []).map((c) => [c.id, c.name]));

    // Fetch Taxes
    const { data: taxes } = await supabase
      .from("taxes")
      .select("id:id_taxes,rate");
    const taxMap = new Map((taxes || []).map((t) => [t.id, t.rate]));

    let imagesByProduct = new Map();
    if (ids.length) {
      const { data: imgs } = await supabase
        .from("product_images")
        .select("product_id,image_base64,mime,created_at")
        .in("product_id", ids)
        .order("created_at", { ascending: false });
      (imgs || []).forEach((img) => {
        if (!imagesByProduct.has(img.product_id))
          imagesByProduct.set(img.product_id, img);
      });
    }

    raw.value = (prods || []).map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description || "",
      price: calculateFinalPrice(p.unit_price, p.discount, taxMap.get(p.tax_id)),
      category: catMap.get(p.category_id) || "Otros",
      image: imagesByProduct.get(p.id) || null,
    }));

    // Set initial category to first available or 'todos'
    if (categories.value.length > 0) {
      activeCategory.value = categories.value[0].id;
    }

  } catch (e) {
    console.error(e);
    errorMsg.value = "No se pudo cargar el menú.";
  } finally {
    loading.value = false;
  }
}

onMounted(fetchMenu);

// Categorization Logic
const categories = computed(() => {
  const cats = new Set();
  raw.value.forEach(p => cats.add(p.category));
  const list = Array.from(cats).sort().map(c => ({
    id: normalizeId(c),
    label: c
  }));
  // Ensure basic order if present
  const priorities = ['platos fuertes', 'hamburguesas', 'pastas', 'bebidas'];
  list.sort((a, b) => {
    const ia = priorities.indexOf(a.label.toLowerCase());
    const ib = priorities.indexOf(b.label.toLowerCase());
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.label.localeCompare(b.label);
  });
  return [{ id: 'todos', label: 'Todos' }, ...list];
});

const filteredProducts = computed(() => {
  if (activeCategory.value === 'todos') return raw.value;
  return raw.value.filter(p => normalizeId(p.category) === activeCategory.value);
});

function normalizeId(str) {
  return String(str || "").toLowerCase().replace(/\s+/g, '-').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function scrollToCategory(catId) {
  activeCategory.value = catId;
  // Optional: scroll to section if we were doing vertical layout
}

function getFallbackIcon(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("hamburg")) return "lunch_dining";
  if (c.includes("pasta")) return "restaurant";
  if (c.includes("bebida") || c.includes("refresco")) return "local_bar";
  if (c.includes("cafe")) return "coffee";
  if (c.includes("postre")) return "cake";
  return "restaurant_menu";
}
</script>

<template>
  <div class="menu-page">
    <div class="menu-header">
      <h1>Nuestro Menú</h1>
      <p class="subtitle">Descubre nuestros deliciosos platillos</p>
    </div>

    <!-- Sticky Category Nav -->
    <div class="category-nav-wrapper">
      <nav class="category-nav">
        <button v-for="cat in categories" :key="cat.id" class="cat-pill" :class="{ active: activeCategory === cat.id }"
          @click="scrollToCategory(cat.id)">
          {{ cat.label }}
        </button>
      </nav>
    </div>

    <!-- Content -->
    <div class="menu-container">
      <div v-if="loading" class="loading-state">
        <span class="spinner"></span>
        <p>Preparando el menú...</p>
      </div>

      <div v-else-if="errorMsg" class="error-state">
        <span class="material-symbols-rounded icon">error</span>
        <p>{{ errorMsg }}</p>
        <button class="retry-btn" @click="fetchMenu">Reintentar</button>
      </div>

      <div v-else class="products-grid">
        <transition-group name="grid">
          <article v-for="product in filteredProducts" :key="product.id" class="menu-card">
            <div class="card-image">
              <img v-if="product.image"
                :src="`data:${product.image.mime || 'image/jpeg'};base64,${product.image.image_base64}`"
                :alt="product.name" loading="lazy" />
              <div v-else class="image-fallback">
                <span class="material-symbols-rounded">{{ getFallbackIcon(product.category) }}</span>
              </div>
              <div class="category-badge">{{ product.category }}</div>
            </div>

            <div class="card-content">
              <div class="card-header">
                <h3 class="product-name">{{ product.name }}</h3>
                <span class="product-price">${{ product.price.toFixed(2) }}</span>
              </div>
              <p class="product-desc" v-if="product.description">{{ product.description }}</p>
              <p class="product-desc muted" v-else>Delicioso platillo preparado al momento.</p>


            </div>
          </article>
        </transition-group>
      </div>

      <div v-if="!loading && !filteredProducts.length" class="empty-state">
        <span class="material-symbols-rounded icon">restaurant_menu</span>
        <p>No hay productos en esta categoría.</p>
      </div>
    </div>

    <ChatAssistant />
  </div>
</template>

<style scoped>
.menu-page {
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
  padding-bottom: 80px;
  min-height: 100vh;
}

.menu-header {
  text-align: center;
  padding: 3rem 1rem 2rem;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.05) 100%);
}

.menu-header h1 {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0;
  background: var(--accent-gradient);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.02em;
}

.subtitle {
  color: var(--muted);
  font-size: 1.1rem;
  margin-top: 0.5rem;
}

/* Category Nav */
.category-nav-wrapper {
  position: sticky;
  top: 65px;
  /* Adjust based on topbar height */
  z-index: 100;
  background: rgba(var(--bg-rgb), 0.95);
  /* We might need to define bg-rgb or assume var(--bg) is enough */
  background: var(--bg);
  backdrop-filter: blur(12px);
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.category-nav {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 0 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  scrollbar-width: none;
}

.category-nav::-webkit-scrollbar {
  display: none;
}

.cat-pill {
  padding: 0.5rem 1.25rem;
  border-radius: 100px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
}

.cat-pill:hover {
  background: var(--surface-2);
  color: var(--text);
  transform: translateY(-1px);
}

.cat-pill.active {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

/* Grid */
.menu-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
}

/* Cards */
.menu-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 1.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
}

.menu-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  border-color: rgba(198, 146, 61, 0.3);
}

.card-image {
  height: 200px;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: var(--surface-2);
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.menu-card:hover .card-image img {
  transform: scale(1.05);
}

.image-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  opacity: 0.5;
}

.image-fallback span {
  font-size: 4rem;
}

.category-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  gap: 1rem;
}

.product-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  line-height: 1.3;
}

.product-price {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--accent-2);
  white-space: nowrap;
}

.product-desc {
  font-size: 0.9rem;
  color: var(--muted);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.product-desc.muted {
  font-style: italic;
  opacity: 0.7;
}



/* States */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 4rem 1rem;
  color: var(--muted);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-2);
  border-top-color: var(--accent);
  border-radius: 50%;
  display: inline-block;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
}

/* Transitions */
.grid-enter-active,
.grid-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.grid-enter-from,
.grid-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.grid-leave-active {
  position: absolute;
}
</style>
